import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HubSettingsFooter, HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { TextChipInput, type TextChipInputHandle } from "@/components/ui/TextChipInput";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { orderedStringArraysEqual } from "@/lib/settingsCardDirty";
import { formatTimestamp } from "@/lib/utils";
import { ApiError } from "@/lib/api";

import {
  PRE_ENCAPSULATION_HUB_SETTINGS_QUERY_KEY,
  fetchPreEncapsulationHubSettings,
  patchPreEncapsulationHubSettings,
} from "./preEncapsulationSettingsApi";

const PRE_ENCAP_SETTINGS_SECTION_HELP =
  "Pre-Encapsulation module settings stored at workflow.app_settings key hub.manufacturing.pre_encapsulation. The Unserialized tab on the floor page lists open work orders whose finished-goods SKU matches one of the allowed SKUs below.";

const PRE_ENCAP_UNSERIALIZED_SKUS_SECTION_HELP =
  "Each chip is a finished-goods SKU on public.build_order.sku. Matching is case-insensitive after trim. Add every SKU that may receive USA shell rows on the Unserialized tab — for example 88-101-1-PC. At least one SKU is required.";

const preEncapSettingsAdminTitle =
  "Manufacturing management hub administrators can edit Pre-Encapsulation settings. Ask an administrator if you need to change the allowed finished-goods SKUs.";

export function PreEncapsulationSettingsSection() {
  const queryClient = useQueryClient();
  const chipInputRef = useRef<TextChipInputHandle>(null);
  const hubManufacturingManagementSettingsAllowed = useHubModuleAdmin("manufacturing_management");
  const [skuChips, setSkuChips] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const settingsQuery = useQuery({
    queryKey: PRE_ENCAPSULATION_HUB_SETTINGS_QUERY_KEY,
    queryFn: fetchPreEncapsulationHubSettings,
    retry: false,
  });

  useEffect(() => {
    if (settingsQuery.data != null && !initialized) {
      setSkuChips(settingsQuery.data.unserializedWorkOrderFinishedSkus);
      setInitialized(true);
    }
  }, [settingsQuery.data, initialized]);

  const saveMutation = useMutation({
    mutationFn: patchPreEncapsulationHubSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(PRE_ENCAPSULATION_HUB_SETTINGS_QUERY_KEY, data);
      setSkuChips(data.unserializedWorkOrderFinishedSkus);
      setInitialized(true);
    },
  });

  const settingsError =
    settingsQuery.error instanceof ApiError && settingsQuery.error.status === 422
      ? settingsQuery.error.message
      : settingsQuery.error instanceof Error
        ? settingsQuery.error.message
        : null;

  function handleSave() {
    const flushed = chipInputRef.current?.flushDraft() ?? skuChips;
    const nextSkus = flushed.length > 0 ? flushed : skuChips;
    if (nextSkus.length === 0) {
      saveMutation.reset();
      return;
    }
    saveMutation.mutate({ unserializedWorkOrderFinishedSkus: nextSkus });
  }

  const updatedAt = settingsQuery.data?.updatedAt ?? null;

  const isDirty = useMemo(() => {
    if (!initialized || settingsQuery.data == null) {
      return false;
    }
    return !orderedStringArraysEqual(skuChips, settingsQuery.data.unserializedWorkOrderFinishedSkus);
  }, [initialized, settingsQuery.data, skuChips]);

  const saveError =
    saveMutation.error instanceof ApiError
      ? saveMutation.error.message
      : saveMutation.error instanceof Error
        ? saveMutation.error.message
        : null;

  return (
    <HubSettingsSection
      title="Pre-Encapsulation"
      hoverHint={PRE_ENCAP_SETTINGS_SECTION_HELP}
      collapsible
      defaultCollapsed
      sectionId="pre-encapsulation"
    >
      {settingsError != null ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
          {settingsError}
        </p>
      ) : null}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <h3 className="text-sm font-semibold text-slate-900">Unserialized work orders</h3>
          <HoverHintWithGlyph hoverHint={PRE_ENCAP_UNSERIALIZED_SKUS_SECTION_HELP} />
        </div>
        <TextChipInput
            ref={chipInputRef}
            label="Allowed finished-goods SKUs"
            chips={skuChips}
            onChipsChange={setSkuChips}
            placeholder="Add SKU and press Enter"
          disabled={!hubManufacturingManagementSettingsAllowed || settingsQuery.isLoading || saveMutation.isPending}
        />

        <HubSettingsFooter>
          <div className="space-y-2">
            {saveError != null ? (
              <p className="text-sm text-rose-700" role="alert">
                {saveError}
              </p>
            ) : null}
            <HubSaveButton
              type="button"
              dirty={isDirty}
              disabled={!hubManufacturingManagementSettingsAllowed || settingsQuery.isLoading}
              blockedHint={preEncapSettingsAdminTitle}
              isLoading={saveMutation.isPending}
              onClick={handleSave}
            >
              Save Pre-Encapsulation settings
            </HubSaveButton>
          </div>
        </HubSettingsFooter>

        {updatedAt != null ? (
          <p className="text-xs text-slate-500">Last saved {formatTimestamp(updatedAt)}.</p>
        ) : (
          <p className="text-xs text-slate-500">Not saved yet.</p>
        )}
      </div>
    </HubSettingsSection>
  );
}
